/**
 * Rewards platform enumerations.
 *
 * Values use UPPER_SNAKE_CASE and are intended to match the backend GraphQL
 * enum values 1:1. They are deliberately string-valued so they remain stable
 * across serialization boundaries (GraphQL, URLs, persisted rule definitions).
 */

/** Distinguishes the two independent reward audiences. */
export enum MemberType {
  CUSTOMER = "CUSTOMER",
  PROMOTER = "PROMOTER",
}

/** Lifecycle status of a rewards program. */
export enum ProgramStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  ARCHIVED = "ARCHIVED",
}

/** How accrued points expire for a program. */
export enum ExpirationPolicy {
  /** Points never expire. */
  NEVER = "NEVER",
  /** Points expire a fixed duration after they are earned. */
  FIXED_DURATION = "FIXED_DURATION",
  /** All points expire at the end of the calendar year. */
  END_OF_CALENDAR_YEAR = "END_OF_CALENDAR_YEAR",
  /** Points expire after a rolling window of member inactivity. */
  ROLLING_INACTIVITY = "ROLLING_INACTIVITY",
}

/** The qualification window used when evaluating tier thresholds. */
export enum TierQualificationPeriod {
  LIFETIME = "LIFETIME",
  CALENDAR_YEAR = "CALENDAR_YEAR",
  ROLLING_12_MONTHS = "ROLLING_12_MONTHS",
  PROGRAM_PERIOD = "PROGRAM_PERIOD",
}

/** Lifecycle status of an earning rule. */
export enum RuleStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  ARCHIVED = "ARCHIVED",
}

/** Boolean combinator for a condition group. */
export enum LogicalOperator {
  AND = "AND",
  OR = "OR",
}

/**
 * The set of fields an earning-rule condition may reference.
 * Extensible: adding a new field here (plus a matching {@link ConditionValueType})
 * is the only frontend change required to support a new earning dimension.
 */
export enum ConditionField {
  MEMBER_TYPE = "MEMBER_TYPE",
  PURCHASE_TYPE = "PURCHASE_TYPE",
  EVENT_ID = "EVENT_ID",
  EVENT_CATEGORY = "EVENT_CATEGORY",
  PRODUCT_ID = "PRODUCT_ID",
  PRODUCT_CATEGORY = "PRODUCT_CATEGORY",
  PROMOTER_ID = "PROMOTER_ID",
  CUSTOMER_SEGMENT = "CUSTOMER_SEGMENT",
  SALES_CHANNEL = "SALES_CHANNEL",
  CURRENCY = "CURRENCY",
  MIN_PURCHASE_SUBTOTAL = "MIN_PURCHASE_SUBTOTAL",
  MAX_PURCHASE_SUBTOTAL = "MAX_PURCHASE_SUBTOTAL",
  TICKET_QUANTITY = "TICKET_QUANTITY",
  MERCHANDISE_QUANTITY = "MERCHANDISE_QUANTITY",
  BOOKING_TYPE = "BOOKING_TYPE",
  MEMBER_TIER = "MEMBER_TIER",
  FIRST_PURCHASE = "FIRST_PURCHASE",
  REPEAT_PURCHASE = "REPEAT_PURCHASE",
  DATE_RANGE = "DATE_RANGE",
  DAY_OF_WEEK = "DAY_OF_WEEK",
  CAMPAIGN_CODE = "CAMPAIGN_CODE",
  PROMOTER_PERFORMANCE = "PROMOTER_PERFORMANCE",
}

/** Operators available for condition evaluation. */
export enum ConditionOperator {
  EQUALS = "EQUALS",
  NOT_EQUALS = "NOT_EQUALS",
  IN = "IN",
  NOT_IN = "NOT_IN",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  BETWEEN = "BETWEEN",
  CONTAINS = "CONTAINS",
  STARTS_WITH = "STARTS_WITH",
  IS_TRUE = "IS_TRUE",
  IS_FALSE = "IS_FALSE",
}

/** The category of value a condition field expects. Drives the UI editor. */
export enum ConditionValueType {
  STRING = "STRING",
  NUMBER = "NUMBER",
  MONEY = "MONEY",
  BOOLEAN = "BOOLEAN",
  DATE = "DATE",
  DATE_RANGE = "DATE_RANGE",
  ENTITY = "ENTITY",
  ENUM = "ENUM",
  MULTI_ENTITY = "MULTI_ENTITY",
  MULTI_ENUM = "MULTI_ENUM",
}

/** The type of points award an earning-rule action produces. */
export enum RuleActionType {
  FIXED_POINTS = "FIXED_POINTS",
  POINTS_PER_CURRENCY_UNIT = "POINTS_PER_CURRENCY_UNIT",
  POINTS_PER_ITEM = "POINTS_PER_ITEM",
  PERCENTAGE_MULTIPLIER = "PERCENTAGE_MULTIPLIER",
  FIXED_MULTIPLIER = "FIXED_MULTIPLIER",
  TIER_QUALIFICATION_POINTS = "TIER_QUALIFICATION_POINTS",
  BONUS_POINTS = "BONUS_POINTS",
  PROMOTER_SALES_PERFORMANCE_POINTS = "PROMOTER_SALES_PERFORMANCE_POINTS",
}

/** Rounding applied to fractional points produced by a rule action. */
export enum RuleRounding {
  FLOOR = "FLOOR",
  CEIL = "CEIL",
  ROUND = "ROUND",
}

/** The scope a rule limit constrains. */
export enum RuleLimitType {
  MAX_POINTS_PER_TRANSACTION = "MAX_POINTS_PER_TRANSACTION",
  MAX_POINTS_PER_MEMBER = "MAX_POINTS_PER_MEMBER",
  MAX_POINTS_PER_DAY = "MAX_POINTS_PER_DAY",
  MAX_POINTS_PER_CAMPAIGN = "MAX_POINTS_PER_CAMPAIGN",
  MAX_APPLICATIONS = "MAX_APPLICATIONS",
  ONE_TIME_ONLY = "ONE_TIME_ONLY",
}

/** The originating purchase type for a transaction or condition. */
export enum PurchaseType {
  EVENT_TICKET = "EVENT_TICKET",
  MERCHANDISE = "MERCHANDISE",
  OTA_TRAVEL = "OTA_TRAVEL",
  PROMOTER_SALE = "PROMOTER_SALE",
  OTHER = "OTHER",
}

/** OTA travel booking sub-type. */
export enum BookingType {
  HOTEL = "HOTEL",
  FLIGHT = "FLIGHT",
  CAR_RENTAL = "CAR_RENTAL",
  PACKAGE = "PACKAGE",
  TOUR = "TOUR",
}

/** Sales channel that originated a purchase. */
export enum SalesChannel {
  WEB = "WEB",
  MOBILE_APP = "MOBILE_APP",
  BOX_OFFICE = "BOX_OFFICE",
  CALL_CENTER = "CALL_CENTER",
  PARTNER = "PARTNER",
  PROMOTER_PORTAL = "PROMOTER_PORTAL",
}

/** The type of a reward transaction. */
export enum TransactionType {
  EARN = "EARN",
  REDEEM = "REDEEM",
  MANUAL_CREDIT = "MANUAL_CREDIT",
  MANUAL_DEBIT = "MANUAL_DEBIT",
  EXPIRE = "EXPIRE",
  REVERSE = "REVERSE",
  REFUND_ADJUSTMENT = "REFUND_ADJUSTMENT",
  TIER_ADJUSTMENT = "TIER_ADJUSTMENT",
  BONUS = "BONUS",
}

/** Posting status of a reward transaction. */
export enum TransactionStatus {
  PENDING = "PENDING",
  POSTED = "POSTED",
  REVERSED = "REVERSED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
}

/** The kind of benefit a catalog item grants. Extensible. */
export enum RewardType {
  DISCOUNT = "DISCOUNT",
  COUPON = "COUPON",
  EVENT_TICKET_BENEFIT = "EVENT_TICKET_BENEFIT",
  MERCHANDISE = "MERCHANDISE",
  TRAVEL_BENEFIT = "TRAVEL_BENEFIT",
  FREE_SHIPPING = "FREE_SHIPPING",
  EARLY_ACCESS = "EARLY_ACCESS",
  VIP_ACCESS = "VIP_ACCESS",
  ACCOUNT_CREDIT = "ACCOUNT_CREDIT",
  CUSTOM = "CUSTOM",
}

/** Availability status of a catalog item. */
export enum CatalogItemStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  ARCHIVED = "ARCHIVED",
  COMING_SOON = "COMING_SOON",
  OUT_OF_STOCK = "OUT_OF_STOCK",
}

/** Lifecycle status of a redemption. */
export enum RedemptionStatus {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  FULFILLED = "FULFILLED",
  CANCELLED = "CANCELLED",
  REVERSED = "REVERSED",
  EXPIRED = "EXPIRED",
}

/** Fulfillment progress of a redemption. */
export enum FulfillmentStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/** Reasons available for a manual point adjustment. */
export enum AdjustmentReason {
  GOODWILL = "GOODWILL",
  CORRECTION = "CORRECTION",
  PROMOTION = "PROMOTION",
  COMPENSATION = "COMPENSATION",
  FRAUD_REVERSAL = "FRAUD_REVERSAL",
  MIGRATION = "MIGRATION",
  OTHER = "OTHER",
}

/** Entity types that the audit log records. */
export enum AuditEntityType {
  PROGRAM = "PROGRAM",
  TIER = "TIER",
  RULE = "RULE",
  CATALOG_ITEM = "CATALOG_ITEM",
  MEMBER = "MEMBER",
  REDEMPTION = "REDEMPTION",
  ADJUSTMENT = "ADJUSTMENT",
}

/** Generic actions captured by the audit log. */
export enum AuditAction {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  STATUS_CHANGE = "STATUS_CHANGE",
  ADJUST = "ADJUST",
  FULFILL = "FULFILL",
  CANCEL = "CANCEL",
  REVERSE = "REVERSE",
}

/** Day of week, aligned to ISO-8601 (1 = Monday ... 7 = Sunday). */
export enum DayOfWeek {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}
